import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'

import { useQuery } from '@/hooks'
import {
	CATALOG_IDS,
	ALLOWED_ISOCODE_INT,
	COOKIE_LANGUAGE_KEY,
	COOKIE_CATALOG_ID_KEY,
	COOKIE_CATALOG_NAME_KEY,
	DEFAULT_CATALOG,
	DEFAULT_LANGUAGE,
	LANGUAGE_CODES,
	COOKIE_AUTH_TOKEN,
	COOKIE_AUTH_REFRESH
} from '@/constants'
import {
	getCookie,
	setLanguageCookie,
	setCatalogIdCookie,
	setCatalogNameCookie
} from '@/utils'

import { catalogsActions, i18nActions, uiActions, authActions } from '@/redux/actions'
import { REFRESH_TOKEN } from '@/redux/actions/auth.actions'
import { ModalContext } from '@/contexts/ModalContext'
import { authMiddleware } from '@/utils/authClient.utils'
import useAuth from '@/hooks/useAuth'

const AuthContext = createContext()

// Refreshing consumes the single-use `vibia_refresh` cookie and re-evaluates the
// `authenticateUserV3` toggle (DV-5930's 3-call-site race in auth.service.js /
// UserApi.ts / authClient.utils.js). Calling it on every mount — regardless of
// whether the access token is still valid — multiplies exposure to that race each
// time the strangler bounces an authenticated user into eproject (every '/' visit,
// every full reload). Skip it when the current token still has room to breathe.
const TOKEN_EXPIRY_BUFFER_MS = 30_000

function getTokenExpiryMs(token) {
	try {
		const payload = token.split('.')[1]
		const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
		return typeof exp === 'number' ? exp * 1000 : null
	} catch {
		return null
	}
}

const AuthProvider = (props) => {
	const { children } = props

	const query = useQuery()
	const dispatch = useDispatch()
	const { modalUnmount } = useContext(ModalContext)
	const { authenticate, logout } = useAuth()

	const userAccount = useSelector(state => state.auth.account, shallowEqual)
	const userIsLogged = useSelector(state => state.auth.logged, shallowEqual)
	const defaultCatalog = useSelector(state => state.catalogs.defaultCatalog, shallowEqual)
	const showModalAuth = useSelector(state => state.auth.showModalAuth, shallowEqual)

	const navigation = useSelector(state => state.ui.navigation, shallowEqual)

	const VALID_LANGUAGE_CODES = useMemo(() => new Set(Object.values(LANGUAGE_CODES)), [])
	const isValidLanguageCode = (language) => VALID_LANGUAGE_CODES.has(language)

	const [cookieLangSession, setCookieLangSession] = useState(getCookie(COOKIE_LANGUAGE_KEY))

	const queryLanguage = useMemo(() => {
		const lang = query.get('language')
		return isValidLanguageCode(lang) ? lang : null
	}, [query])

	const cookieLanguage = useMemo(() => {
		const lang = getCookie(COOKIE_LANGUAGE_KEY)
		return isValidLanguageCode(lang) ? lang : null
	}, [])

	const getCatalogFallbackLanguage = (catalog) => {
		if (catalog === CATALOG_IDS.USA) {
			return 'us'
		}

		const browserLanguage = window.navigator.language.split('-')[0]
		const defaultCatalogLanguage = defaultCatalog?.lang?.isoCode
		return ALLOWED_ISOCODE_INT.includes(browserLanguage)
			? browserLanguage
			: (isValidLanguageCode(defaultCatalogLanguage) ? defaultCatalogLanguage : DEFAULT_LANGUAGE)
	}

	useEffect(async () => {
		authMiddleware(logout)
		dispatch(authActions.showModalAuth(false))

		const accessToken = getCookie(COOKIE_AUTH_TOKEN)
		const accessTokenExpiryMs = accessToken ? getTokenExpiryMs(accessToken) : null
		const accessTokenStillValid = accessTokenExpiryMs !== null && accessTokenExpiryMs - Date.now() > TOKEN_EXPIRY_BUFFER_MS

		if (accessTokenStillValid) {
			dispatch(authActions.sessionRestored())
			dispatch(uiActions.setNavigation({ hasBeenActive: true }))
		} else if (accessToken || getCookie(COOKIE_AUTH_REFRESH)) {
			const res = await dispatch(authActions.refreshToken())
			dispatch(uiActions.setNavigation({ hasBeenActive: res?.type === REFRESH_TOKEN.SUCCESS }))
		}

		if (query.get('catalog') || getCookie(COOKIE_CATALOG_ID_KEY) || getCookie(COOKIE_CATALOG_NAME_KEY)) {
			const catalogParam = query.get('catalog')
			let catalog =
				CATALOG_IDS[catalogParam] ||
				(['16', '21'].includes(catalogParam) ? Number(catalogParam) : null) ||
				+getCookie(COOKIE_CATALOG_ID_KEY) ||
				CATALOG_IDS[getCookie(COOKIE_CATALOG_NAME_KEY)]

			const lang = getDefaultLanguage(catalog)
			setCatalogAndLanguage(lang, catalog)
			setCookieLangSession(lang)
		} else {
			const lang = getDefaultLanguage(DEFAULT_CATALOG)
			setCatalogAndLanguage(lang, DEFAULT_CATALOG)
			setCookieLangSession(lang)
			dispatch(catalogsActions.getDefaultCatalog())
		}
	}, [])

	useEffect(() => {
		if (userIsLogged) {
			dispatch(authActions.getAccountDetails())
		}
	}, [userIsLogged])

	useEffect(() => {
		if (userAccount) {
			dispatch(authActions.logged())
		}
	}, [userAccount])

	useEffect(() => {
		if (defaultCatalog?.catalogId) {
			const lang = getDefaultLanguage(defaultCatalog?.catalogId)
			setCatalogAndLanguage(lang, defaultCatalog?.catalogId)
		}
	}, [defaultCatalog?.catalogId])

	useEffect(() => {
		if (!userAccount || navigation.preferredByUser) {
			return
		}

		const catalog =
			+userAccount.catalog ||
			+Object.values(CATALOG_IDS).find((catalogId) => catalogId === CATALOG_IDS[query.get('catalog')]) ||
			+getCookie(COOKIE_CATALOG_ID_KEY) ||
			CATALOG_IDS[getCookie(COOKIE_CATALOG_NAME_KEY)] ||
			DEFAULT_CATALOG
		const language =
			(isValidLanguageCode(userAccount.lang) ? userAccount.lang : null) ||
			queryLanguage ||
			cookieLanguage ||
			getCatalogFallbackLanguage(catalog)

		setCatalogAndLanguage(language, catalog)
	}, [userAccount])

	const setCatalogAndLanguage = (language, catalog) => {
		let safeLanguage = isValidLanguageCode(language) ? language : getCatalogFallbackLanguage(catalog)
		// Enforce catalog↔language invariant: USA only with 'us', INT never with 'us'
		if (catalog === CATALOG_IDS.USA && safeLanguage !== 'us') safeLanguage = 'us'
		if (catalog !== CATALOG_IDS.USA && safeLanguage === 'us') safeLanguage = getCatalogFallbackLanguage(catalog)

		dispatch(i18nActions.setLocale(safeLanguage))
		dispatch(uiActions.setNavigation({ language: safeLanguage, catalog }))
		dispatch(catalogsActions.selectCatalog(catalog))
		setLanguageCookie(safeLanguage)
		setCatalogIdCookie(catalog)
		setCatalogNameCookie(catalog)
	}

	const getDefaultLanguage = (catalog) => {
		return queryLanguage || cookieLanguage || getCatalogFallbackLanguage(catalog)
	}

	useEffect(() => {
		if (showModalAuth) {
			authenticate()
		} else {
			modalUnmount()
		}
	}, [showModalAuth])


	return (
		<AuthContext.Provider
			value={{}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export { AuthContext, AuthProvider }
